import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
  ComputeBudgetProgram,
  VersionedTransaction,
  TransactionMessage,
} from '@solana/web3.js';
import {
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import {
  ApiV3,
  Liquidity,
  LiquidityPoolKeys,
  Token,
  TokenAmount,
  Percent,
  LiquidityPoolKeysV4,
  MAINNET_PROGRAM_ID,
  DEVNET_PROGRAM_ID,
  LOOKUP_TABLE_CACHE,
  TxVersion,
  TokenMath,
} from '@raydium-io/raydium-sdk';
import { logger } from '@/lib/logger';

export interface DEXConfig {
  name: string;
  programId: PublicKey;
  authority: PublicKey;
  fee: number; // basis points
  minLiquidity: number;
}

export interface TradeParams {
  inputMint: PublicKey;
  outputMint: PublicKey;
  inputAmount: bigint;
  slippageBps?: number;
  maxAccounts?: number;
  userWallet: PublicKey;
}

export interface SwapQuote {
  inputAmount: bigint;
  outputAmount: bigint;
  priceImpactPct: number;
  feeAmount: bigint;
  routes: SwapRoute[];
  ctx: {
    slot: bigint;
  };
}

export interface SwapRoute {
  inputMint: PublicKey;
  outputMint: PublicKey;
  inputAmount: bigint;
  outputAmount: bigint;
  feeAmount: bigint;
  feeMint: PublicKey;
}

export interface JupiterSwapResult {
  success: boolean;
  transaction?: VersionedTransaction;
  quote?: SwapQuote;
  error?: string;
}

// Jupiter API Integration
export class JupiterDEXAggregator {
  private readonly baseUrl = 'https://quote-api.jup.ag/v6';

  constructor(private connection: Connection) { }

  /**
   * Get swap quote from Jupiter
   */
  async getQuote(params: TradeParams): Promise<SwapQuote> {
    const url = new URL(`${this.baseUrl}/quote`);

    url.searchParams.set('inputMint', params.inputMint.toString());
    url.searchParams.set('outputMint', params.outputMint.toString());
    url.searchParams.set('amount', params.inputAmount.toString());
    url.searchParams.set('slippageBps', (params.slippageBps || 50).toString());
    url.searchParams.set('maxAccounts', (params.maxAccounts || 20).toString());
    url.searchParams.set('onlyDirectRoutes', 'true');
    url.searchParams.set('asLegacyTransaction', 'false');

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Jupiter API error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Build swap transaction
   */
  async buildSwapTransaction(
    quote: SwapQuote,
    userWallet: PublicKey,
    maxLamports?: number
  ): Promise<VersionedTransaction> {
    const url = new URL(`${this.baseUrl}/swap`);

    url.searchParams.set('quoteResponse', JSON.stringify(quote));
    url.searchParams.set('userPublicKey', userWallet.toString());
    url.searchParams.set('wrapAndUnwrapSol', 'true');

    if (maxLamports) {
      url.searchParams.set('maxLamports', maxLamports.toString());
    }

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Jupiter swap transaction error: ${response.status} ${response.statusText}`);
    }

    const swapTransaction = await response.arrayBuffer();
    return VersionedTransaction.deserialize(new Uint8Array(swapTransaction));
  }

  /**
   * Execute swap with Jupiter
   */
  async executeSwap(params: TradeParams): Promise<JupiterSwapResult> {
    try {
      logger.info('Getting Jupiter quote', {
        inputMint: params.inputMint.toString(),
        outputMint: params.outputMint.toString(),
        amount: params.inputAmount.toString()
      });

      const quote = await this.getQuote(params);

      logger.info('Jupiter quote received', {
        inputAmount: quote.inputAmount.toString(),
        outputAmount: quote.outputAmount.toString(),
        priceImpact: quote.priceImpactPct,
      });

      // Reject trades with high price impact
      if (quote.priceImpactPct > 5) { // 5% max price impact
        return {
          success: false,
          error: `Price impact too high: ${quote.priceImpactPct.toFixed(2)}%`,
        };
      }

      const transaction = await this.buildSwapTransaction(quote, params.userWallet);

      // Add priority fees for faster execution
      const computeBudgetInstruction = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1000, // 1000 micro-lamports = 0.000001 SOL
      });

      // Note: VersionedTransaction doesn't support add() method directly
      // The priority fee is handled by Jupiter's API

      return {
        success: true,
        transaction,
        quote,
      };
    } catch (error) {
      logger.error('Jupiter swap failed', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Raydium DEX Integration
export class RaydiumDEX {
  private readonly programId = MAINNET_PROGRAM_ID.AmmV4;
  private readonly devnetProgramId = DEVNET_PROGRAM_ID.AmmV4;

  constructor(private connection: Connection) { }

  /**
   * Get Raydium pool info from API or on-chain
   */
  async getPoolInfo(inputMint: PublicKey, outputMint: PublicKey): Promise<LiquidityPoolKeysV4 | null> {
    try {
      // In production, we'd use Raydium's API to find the pool
      // For this implementation, we'll search for the pool on-chain or use a simplified version of Liquidity.fetchAllPoolKeys
      // Since searching all pools is slow, we'll use a simplified version of Liquidity.fetchAllPoolKeys

      const isDevnet = this.connection.rpcEndpoint.includes('devnet');
      const targetProgramId = isDevnet ? this.devnetProgramId : this.programId;

      const poolKeys = await Liquidity.fetchAllPoolKeys(this.connection, {
        programId: targetProgramId,
      });

      const pool = poolKeys.find(p =>
        (p.baseMint.equals(inputMint) && p.quoteMint.equals(outputMint)) ||
        (p.baseMint.equals(outputMint) && p.quoteMint.equals(inputMint))
      );

      return pool || null;
    } catch (error) {
      logger.error('Failed to fetch Raydium pool info', error);
      return null;
    }
  }

  /**
   * Execute Raydium swap
   */
  async executeSwap(params: TradeParams): Promise<JupiterSwapResult> {
    try {
      const poolKeys = await this.getPoolInfo(params.inputMint, params.outputMint);

      if (!poolKeys) {
        return {
          success: false,
          error: 'No Raydium pool found for this pair',
        };
      }

      // Fetch pool state
      const poolInfo = await Liquidity.fetchInfo({
        connection: this.connection,
        poolKeys,
      });

      // Fetch decimals for both tokens
      const inputMintInfo = await this.connection.getParsedAccountInfo(params.inputMint);
      const outputMintInfo = await this.connection.getParsedAccountInfo(params.outputMint);

      const inputDecimals = (inputMintInfo.value?.data as any)?.parsed?.info?.decimals ?? 9;
      const outputDecimals = (outputMintInfo.value?.data as any)?.parsed?.info?.decimals ?? 9;

      const slippage = new Percent(params.slippageBps || 50, 10000); // Default 0.5%

      // Calculate amount out
      const { amountOut, minAmountOut, currentPrice, priceImpact, fee } = Liquidity.computeAmountOut({
        poolKeys,
        poolInfo,
        amountIn: new TokenAmount(new Token(TOKEN_PROGRAM_ID, params.inputMint, inputDecimals), params.inputAmount),
        currencyOut: new Token(TOKEN_PROGRAM_ID, params.outputMint, outputDecimals),
        slippage,
      });

      if (priceImpact.greaterThan(new Percent(500, 10000))) { // 5%
        return {
          success: false,
          error: `Price impact too high: ${priceImpact.toSignificant()}%`,
        };
      }

      // Build transaction
      const { innerTransactions } = await Liquidity.makeSwapInstructionSimple({
        connection: this.connection,
        poolKeys,
        userKeys: {
          owner: params.userWallet,
          payer: params.userWallet,
          tokenAccounts: [], // SDK will find or create
        },
        amountIn: new TokenAmount(new Token(TOKEN_PROGRAM_ID, params.inputMint, inputDecimals), params.inputAmount),
        amountOut: minAmountOut,
        fixedSide: 'in',
        makeTxVersion: TxVersion.V0,
      });

      // Convert inner transactions to a single VersionedTransaction
      const { blockhash } = await this.connection.getLatestBlockhash();
      const messageV0 = new TransactionMessage({
        payerKey: params.userWallet,
        recentBlockhash: blockhash,
        instructions: innerTransactions.flatMap(it => it.instructions),
      }).compileToV0Message();

      const transaction = new VersionedTransaction(messageV0);

      const quote: SwapQuote = {
        inputAmount: params.inputAmount,
        outputAmount: BigInt(amountOut.raw.toString()),
        priceImpactPct: parseFloat(priceImpact.toSignificant()),
        feeAmount: BigInt(fee.raw.toString()),
        routes: [{
          inputMint: params.inputMint,
          outputMint: params.outputMint,
          inputAmount: params.inputAmount,
          outputAmount: BigInt(amountOut.raw.toString()),
          feeAmount: BigInt(fee.raw.toString()),
          feeMint: params.inputMint,
        }],
        ctx: {
          slot: BigInt(await this.connection.getSlot()),
        },
      };

      return {
        success: true,
        transaction,
        quote,
      };
    } catch (error) {
      logger.error('Raydium swap failed', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// DEX Aggregator
export class DEXAggregator {
  private jupiter: JupiterDEXAggregator;
  private raydium: RaydiumDEX;

  constructor(private connection: Connection) {
    this.jupiter = new JupiterDEXAggregator(connection);
    this.raydium = new RaydiumDEX(connection);
  }

  /**
   * Get best quote from all DEXes
   */
  async getBestQuote(params: TradeParams): Promise<JupiterSwapResult> {
    const results = await Promise.allSettled([
      this.jupiter.executeSwap(params),
      this.raydium.executeSwap(params),
    ]);

    // Filter successful results and find the best one
    const successfulResults = results
      .filter((result): result is PromiseFulfilledResult<JupiterSwapResult> =>
        result.status === 'fulfilled' && result.value.success
      )
      .map(result => result.value);

    if (successfulResults.length === 0) {
      return {
        success: false,
        error: 'All DEXes failed to provide a quote',
      };
    }

    // Find the best output amount
    let bestResult = successfulResults[0];
    let bestOutputAmount = bestResult.quote?.outputAmount || BigInt(0);

    for (const result of successfulResults.slice(1)) {
      if (result.quote && result.quote.outputAmount > bestOutputAmount) {
        bestResult = result;
        bestOutputAmount = result.quote.outputAmount;
      }
    }

    return bestResult;
  }

  /**
   * Execute swap on best DEX
   */
  async executeSwap(params: TradeParams): Promise<JupiterSwapResult> {
    logger.info('Executing DEX swap', {
      inputMint: params.inputMint.toString(),
      outputMint: params.outputMint.toString(),
      amount: params.inputAmount.toString(),
    });

    const result = await this.getBestQuote(params);

    if (result.success) {
      logger.info('DEX swap successful', {
        inputAmount: result.quote?.inputAmount.toString(),
        outputAmount: result.quote?.outputAmount.toString(),
        priceImpact: result.quote?.priceImpactPct,
      });
    }

    return result;
  }
}

// Utility functions
export function isSolToken(mint: PublicKey): boolean {
  return mint.equals(SystemProgram.programId);
}

export function getSolMint(): PublicKey {
  return SystemProgram.programId;
}

export async function getTokenBalance(
  connection: Connection,
  wallet: PublicKey,
  mint: PublicKey
): Promise<bigint> {
  if (isSolToken(mint)) {
    return BigInt(await connection.getBalance(wallet));
  }

  try {
    const tokenAccount = await getAssociatedTokenAddress(mint, wallet);
    const accountInfo = await connection.getAccountInfo(tokenAccount);

    if (!accountInfo) {
      return BigInt(0);
    }

    // Parse token account data - simplified for example
    // In production, use proper Token Account parser
    const balance = accountInfo.data.readBigUInt64LE(64); // Balance is at offset 64
    return balance;
  } catch (error) {
    logger.error('Failed to get token balance', error);
    return BigInt(0);
  }
}